<template>
    <div class="assessment-page">
        <!-- 背景 -->
        <img class="bg-img" src="/static/t2.png" alt="" />
        <div class="overlay"></div>

        <!-- 内容区域 -->
        <div class="content">
            <!-- 进度条 -->
            <div class="progress-bar">
                <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
            </div>
            <span class="progress-text">{{ currentIndex + 1 }} / {{ questions.length }}</span>

            <!-- 问题卡片 -->
            <div class="question-card" v-if="currentQuestion">
                <span class="question-num">问题 {{ currentIndex + 1 }}</span>
                <span class="question-text">{{ currentQuestion.question }}</span>
            </div>

            <!-- 答案输入 -->
            <div class="answer-area">
                <textarea
                    class="answer-input"
                    v-model="currentAnswer"
                    placeholder="请输入您的回答..."
                    maxlength="500"
                ></textarea>
                <span class="char-count">{{ currentAnswer.length }}/500</span>
            </div>

            <!-- 操作按钮 -->
            <div class="action-btns">
                <div class="btn prev-btn" v-if="currentIndex > 0" @click="prevQuestion">
                    <span>上一题</span>
                </div>
                <div class="btn next-btn" :class="{ disabled: !canSubmit }" @click="submitAnswer">
                    <span>{{ isLastQuestion ? '完成' : '下一题' }}</span>
                </div>
            </div>
        </div>

        <!-- 完成弹窗 -->
        <div class="complete-modal" v-if="showComplete">
            <div class="complete-content">
                <span class="complete-icon">✓</span>
                <span class="complete-title">评估完成</span>
                <span class="complete-desc">感谢您的配合，评估结果将用于更好地帮助您</span>
                <div class="complete-btn" @click="goHome">
                    <span>返回首页</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { request } from '@/utils/request.js';

export default {
    data() {
        return {
            questions: [], // 所有问题列表
            currentIndex: 0, // 当前问题索引
            currentAnswer: '', // 当前答案
            answers: {}, // 已回答的问题 { questionId: answer }
            showComplete: false, // 是否显示完成弹窗
            isSubmitting: false, // 是否正在提交
        };
    },
    computed: {
        // 当前问题
        currentQuestion() {
            return this.questions[this.currentIndex] || null;
        },
        // 是否是最后一题
        isLastQuestion() {
            return this.currentIndex >= this.questions.length - 1;
        },
        // 进度百分比
        progressPercent() {
            if (this.questions.length === 0) return 0;
            return ((this.currentIndex + 1) / this.questions.length) * 100;
        },
        // 是否可以提交
        canSubmit() {
            return this.currentAnswer.trim().length > 0 && !this.isSubmitting;
        },
    },
    // 页面加载时先拉取题目，再恢复答题进度（对应 uniapp 的 onLoad）
    async created() {
        await this.loadQuestions();
        await this.loadProgress();
    },
    methods: {
        // 加载问题列表
        async loadQuestions() {
            try {
                const res = await request({
                    url: '/api/assessment/questions',
                    method: 'GET',
                });
                if (res.data.code === 200) {
                    this.questions = res.data.data.questions;
                }
            } catch (e) {
                console.error('加载问题失败:', e);
                this.$toast('加载问题失败');
            }
        },

        // 加载已回答进度
        async loadProgress() {
            try {
                const res = await request({
                    url: '/api/assessment/progress',
                    method: 'GET',
                });
                if (res.data.code === 200) {
                    const data = res.data.data;
                    // 恢复已回答的内容
                    if (data.answers && data.answers.length > 0) {
                        data.answers.forEach((a) => {
                            this.answers[a.question_id] = a.answer;
                        });
                    }
                    // 设置当前问题索引
                    if (data.currentQuestionIndex < this.questions.length) {
                        this.currentIndex = data.currentQuestionIndex;
                        // 恢复当前答案
                        const qid =
                            this.questions[this.currentIndex] &&
                            this.questions[this.currentIndex].id;
                        if (qid && this.answers[qid]) {
                            this.currentAnswer = this.answers[qid];
                        }
                    }
                }
            } catch (e) {
                console.error('加载进度失败:', e);
            }
        },

        // 提交答案
        async submitAnswer() {
            if (!this.canSubmit) return;

            this.isSubmitting = true;
            try {
                const res = await request({
                    url: '/api/assessment/answer',
                    method: 'POST',
                    data: {
                        questionId: this.currentQuestion.id,
                        answer: this.currentAnswer.trim(),
                    },
                });

                if (res.data.code === 200) {
                    // 保存答案到本地
                    this.answers[this.currentQuestion.id] = this.currentAnswer.trim();

                    if (this.isLastQuestion) {
                        // 完成评估
                        await this.completeAssessment();
                    } else {
                        // 下一题
                        this.currentIndex++;
                        this.currentAnswer = '';
                        // 检查是否有已保存的答案
                        const qid =
                            this.questions[this.currentIndex] &&
                            this.questions[this.currentIndex].id;
                        if (qid && this.answers[qid]) {
                            this.currentAnswer = this.answers[qid];
                        }
                    }
                } else {
                    this.$toast(res.data.message || '提交失败');
                }
            } catch (e) {
                console.error('提交答案失败:', e);
                this.$toast('提交失败');
            } finally {
                this.isSubmitting = false;
            }
        },

        // 上一题
        prevQuestion() {
            if (this.currentIndex > 0) {
                this.currentIndex--;
                // 恢复上一题的答案
                const qid =
                    this.questions[this.currentIndex] && this.questions[this.currentIndex].id;
                if (qid && this.answers[qid]) {
                    this.currentAnswer = this.answers[qid];
                } else {
                    this.currentAnswer = '';
                }
            }
        },

        // 完成评估
        async completeAssessment() {
            try {
                const res = await request({
                    url: '/api/assessment/complete',
                    method: 'POST',
                });
                if (res.data.code === 200) {
                    this.showComplete = true;
                }
            } catch (e) {
                console.error('完成评估失败:', e);
                // 即使接口失败也显示完成
                this.showComplete = true;
            }
        },

        // 返回聊天首页
        goHome() {
            this.$router.replace('/chat');
        },
    },
};
</script>

<style lang="scss" scoped>
.assessment-page {
    width: 100vw;
    height: 100vh;
    position: relative;
    overflow: hidden;
}

// 背景图（image aspectFill 对应 object-fit:cover）
.bg-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    z-index: 1;
}

.overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.3);
    z-index: 2;
}

.content {
    position: relative;
    z-index: 3;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 80rpx 40rpx 60rpx;
    box-sizing: border-box;
}

// 进度条
.progress-bar {
    width: 100%;
    height: 12rpx;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 6rpx;
    overflow: hidden;
    margin-bottom: 20rpx;
}

.progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #ff9a6c, #e8735a);
    border-radius: 6rpx;
    transition: width 0.3s ease;
}

.progress-text {
    font-size: 26rpx;
    color: rgba(255, 255, 255, 0.9);
    text-align: center;
    display: block;
    margin-bottom: 40rpx;
}

// 问题卡片
.question-card {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 24rpx;
    padding: 40rpx;
    margin-bottom: 30rpx;
    box-shadow: 0 8rpx 32rpx rgba(0, 0, 0, 0.1);
}

.question-num {
    font-size: 26rpx;
    color: #e8735a;
    display: block;
    margin-bottom: 20rpx;
}

.question-text {
    font-size: 34rpx;
    color: #3d2c2c;
    line-height: 1.6;
    display: block;
}

// 答案输入
.answer-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 24rpx;
    padding: 30rpx;
    margin-bottom: 30rpx;
    box-shadow: 0 8rpx 32rpx rgba(0, 0, 0, 0.1);
}

// 文本域（H5 需重置原生样式，去掉了小程序的 auto-height）
.answer-input {
    flex: 1;
    width: 100%;
    min-height: 200rpx;
    font-size: 30rpx;
    color: #3d2c2c;
    line-height: 1.6;
    border: none;
    outline: none;
    resize: none;
    background: transparent;
    padding: 0;
    box-sizing: border-box;
    font-family: inherit;
}

.char-count {
    font-size: 24rpx;
    color: #aaa;
    text-align: right;
    margin-top: 16rpx;
}

// 操作按钮（gap 改为相邻 margin，兼容旧浏览器）
.action-btns {
    display: flex;
}

.btn + .btn {
    margin-left: 24rpx;
}

.btn {
    flex: 1;
    height: 96rpx;
    border-radius: 48rpx;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;

    span {
        font-size: 32rpx;
        font-weight: 500;
    }

    &:active {
        transform: scale(0.97);
    }
}

.prev-btn {
    background: rgba(255, 255, 255, 0.9);

    span {
        color: #666;
    }
}

.next-btn {
    background: linear-gradient(135deg, #ff9a6c 0%, #e8735a 100%);
    box-shadow: 0 8rpx 24rpx rgba(232, 115, 90, 0.35);

    span {
        color: #fff;
    }

    &.disabled {
        opacity: 0.5;
    }
}

// 完成弹窗
.complete-modal {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 100;
}

.complete-content {
    width: 80%;
    max-width: 560rpx;
    background: #fff;
    border-radius: 24rpx;
    padding: 60rpx 40rpx;
    text-align: center;
}

.complete-icon {
    width: 100rpx;
    height: 100rpx;
    background: linear-gradient(135deg, #ff9a6c, #e8735a);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 50rpx;
    color: #fff;
    margin: 0 auto 30rpx;
}

.complete-title {
    font-size: 36rpx;
    font-weight: 600;
    color: #3d2c2c;
    display: block;
    margin-bottom: 20rpx;
}

.complete-desc {
    font-size: 28rpx;
    color: #888;
    display: block;
    margin-bottom: 40rpx;
    line-height: 1.5;
}

.complete-btn {
    height: 88rpx;
    background: linear-gradient(135deg, #ff9a6c 0%, #e8735a 100%);
    border-radius: 44rpx;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 8rpx 24rpx rgba(232, 115, 90, 0.35);

    span {
        font-size: 30rpx;
        color: #fff;
        font-weight: 500;
    }

    &:active {
        transform: scale(0.97);
    }
}
</style>
